import { acceptEventAndAddMessage } from '../strings.locstring.json';
import {
    acceptOccurrenceAndAddMessage,
    acceptSeriesAndAddMessage,
    tentativelyAcceptOccurrenceAndAddMessage,
    tentativelyAcceptSeriesAndAddMessage,
    declineOccurrenceAndAddMessage,
    declineSeriesAndAddMessage,
} from './getMeetingResponseMenuItems.locstring.json';
import { declineEventAndAddMessage } from 'owa-locstrings/lib/strings/declineeventandaddmessage.locstring.json';
import { tentativelyAcceptEventAndAddMessage } from 'owa-locstrings/lib/strings/tentativelyaccepteventandaddmessage.locstring.json';
import { allEventsInSeries } from 'owa-locstrings/lib/strings/alleventsinseries.locstring.json';
import { thisEvent } from 'owa-locstrings/lib/strings/thisevent.locstring.json';
import loc from 'owa-localize';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';

import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import EventScope from 'owa-service/lib/contract/EventScope';
import * as CalendarItemCapabilities from 'owa-calendar-event-capabilities';

/**
 * Creates the meeting response menu items
 * @param item the calendar event
 * @param newResponseType the response type to be making this sub menu for
 * @param onResponseClicked the callback function to fire after clicking on a sub menu item. The implementation of this callback
 * function needs to handle that variations on item.IsResponseRequested
 */
export default function getMeetingResponseMenuItems(
    item: CalendarEvent,
    newResponseType: ResponseTypeType,
    onResponseClicked: (
        responseType: ResponseTypeType,
        eventScope: EventScope,
        editorTitleText: string
    ) => void
): IContextualMenuItem[] {
    const isSeriesInstance = CalendarItemCapabilities.isSeriesInstance(item);
    const isSeriesMaster = CalendarItemCapabilities.isSeriesMaster(item);

    var menuTitleText = loc(thisEvent);
    var seriesMenuTitleText = loc(allEventsInSeries);
    var editorTitleText;
    var seriesEditorTitleText;

    /**
     * Server work is not finished for declining a single instance of a recurring series. For now, just disable that option.
     */
    const allowAcceptTentativeSeriesInstance = !(
        (newResponseType === 'Accept' || newResponseType === 'Tentative') &&
        isSeriesInstance &&
        item.ResponseType === 'Decline'
    );

    switch (newResponseType) {
        case 'Accept':
            editorTitleText = isSeriesInstance
                ? loc(acceptOccurrenceAndAddMessage)
                : loc(acceptEventAndAddMessage);
            seriesEditorTitleText = loc(acceptSeriesAndAddMessage);
            break;
        case 'Tentative':
            editorTitleText = isSeriesInstance
                ? loc(tentativelyAcceptOccurrenceAndAddMessage)
                : loc(tentativelyAcceptEventAndAddMessage);
            seriesEditorTitleText = loc(tentativelyAcceptSeriesAndAddMessage);
            break;
        case 'Decline':
            editorTitleText = isSeriesInstance
                ? loc(declineOccurrenceAndAddMessage)
                : loc(declineEventAndAddMessage);
            seriesEditorTitleText = loc(declineSeriesAndAddMessage);
            break;
    }

    let menuItems: IContextualMenuItem[] = [];

    if (item.IsResponseRequested) {
        menuItems.push({
            key: 'edit',
            name: menuTitleText,
            onClick: () => {
                onResponseClicked(
                    newResponseType,
                    EventScope.ThisInstanceOnly,
                    isSeriesMaster ? seriesEditorTitleText : editorTitleText
                );
            },
            disabled: !allowAcceptTentativeSeriesInstance,
        });
        if (isSeriesInstance) {
            menuItems.push({
                key: 'series_edit',
                name: seriesMenuTitleText,
                onClick: () => {
                    onResponseClicked(
                        newResponseType,
                        EventScope.AllInstancesInSeries,
                        seriesEditorTitleText
                    );
                },
            });
        }
    } else {
        menuItems.push({
            key: 'actOnInstance',
            name: menuTitleText,
            onClick: () => {
                onResponseClicked(
                    newResponseType,
                    EventScope.ThisInstanceOnly,
                    isSeriesMaster ? seriesEditorTitleText : editorTitleText
                );
            },
            disabled: !allowAcceptTentativeSeriesInstance,
        });

        if (isSeriesInstance) {
            menuItems.push({
                key: 'actOnSeries',
                name: seriesMenuTitleText,
                onClick: () => {
                    onResponseClicked(
                        newResponseType,
                        EventScope.AllInstancesInSeries,
                        seriesEditorTitleText
                    );
                },
            });
        }
    }

    return menuItems;
}
